import React, { Component, Fragment } from "react";
import cn from "classnames";
import { FormattedMessage } from "react-intl";

import { IRhytSortingData, ITaskEditorProps } from "../../../../../../../common/types";
import { rhytCardTypes } from "../../../../../../../common/parameters";

import Rhythms from "../../svg";
import Box from "../../Box";
import { Bar as StyledBar, RemoveBox as AddBox } from "../../Box/styles";

import { Editor, Bars, Btns, Btn, Break } from "./styles";
import messages from "./messages";
import { IState } from "./interfaces";
import getScore from "../../../Common/getScore";
import { parseScoreToBoxes } from "../../utils";

class RhytCardEditor extends Component<ITaskEditorProps, IState> {
    saveInterval: ReturnType<typeof setInterval>;
    readyToSave = false;
    constructor(props: ITaskEditorProps) {
        super(props);
        const { plan, taskIndex } = props;

        let boxes: string[][] = [[]];
        let breaks: number[] = [];
        const task = plan.tasks[taskIndex];
        const data = task.data as IRhytSortingData;
        if (data) {
            if (data.boxes && data.boxes.length) {
                boxes = data.boxes;
                breaks = data.breaks || [];
            } else if (task.ref) {
                const score = getScore(plan, task);
                ({ bars: boxes, breaks } = parseScoreToBoxes(score));
            }
        }

        this.state = {
            boxes,
            breaks,
            active: 0,
        };
        this.saveInterval = setInterval(this.save, 1.5 * 1000);
    }

    componentWillUnmount() {
        this.save();
        clearInterval(this.saveInterval);
    }

    save = () => {
        if (!this.readyToSave) return;
        const { plan, editTask, taskIndex, post } = this.props;
        const { boxes, breaks } = this.state;
        const saveBoxes = [...boxes];
        if (saveBoxes[saveBoxes.length - 1].length === 0) saveBoxes.pop();
        editTask({ taskIndex, changingProp: "boxes", val: saveBoxes });
        editTask({ taskIndex, changingProp: "breaks", val: breaks });
        post("rhyt-sorting-boxes", {
            planId: plan._id,
            index: taskIndex,
            boxes: saveBoxes,
            breaks,
        });
        this.readyToSave = false;
    };

    addRhytToBox = (rhyt: string) => {
        const { boxes, active } = this.state;
        boxes[active].push(rhyt);
        this.setState({ boxes });
        this.readyToSave = true;
    };

    removeRhyt = (index: number, rhytIndex: number) => {
        let { boxes, active, breaks } = this.state;
        boxes[index].splice(rhytIndex, 1);
        if (boxes[index].length === 0 && boxes.length !== 1) {
            boxes.splice(index, 1);
            active = Math.max(--active, 0);
            breaks = breaks
                .filter(b => b !== index)
                .map(b => {
                    if (b > index) {
                        return b - 1;
                    }
                    return b;
                });
        }
        this.setState({ boxes, active, breaks });
        this.readyToSave = true;
    };

    addBox = () => {
        const { boxes } = this.state;
        boxes.push([]);
        let active = boxes.length - 1;
        this.setState({ boxes, active });
        this.readyToSave = true;
    };

    removeBox = (index: number) => {
        let { boxes, active } = this.state;
        if (active === index) active--;
        boxes.splice(index, 1);
        this.setState({ boxes, active });
        this.readyToSave = true;
    };

    changeOrder = (index: number, direction: number) => {
        let { boxes, active, breaks } = this.state;
        if (active === index) {
            active += direction;
        }
        boxes.splice(index + direction, 0, boxes.splice(index, 1)[0]);
        breaks = breaks.map(b => {
            if (b === index) {
                return b + direction;
            } else if (b === index + direction) {
                return b - direction;
            }
            return b;
        });
        this.setState({ boxes, active, breaks });
        this.readyToSave = true;
    };
    setActive = (index: number) => {
        let { boxes, active } = this.state;
        if (index !== active) {
            if (active === boxes.length - 1 && boxes[boxes.length - 1].length === 0) {
                boxes.pop();
            }
            this.setState({ active: index, boxes });
        }
    };

    onLinebreakClick = (index: number) => {
        const { breaks } = this.state;
        let breakIndex = breaks.indexOf(index);
        if (breakIndex > -1) {
            this.setState({
                breaks: breaks.filter(i => i !== index),
            });
        } else {
            this.setState({
                breaks: [...breaks, index],
            });
        }
        this.readyToSave = true;
    };

    render() {
        const { boxes, active, breaks } = this.state;
        return (
            <Editor>
                <Bars>
                    {boxes.map((box, index) => {
                        return (
                            <Fragment key={index}>
                                <Box
                                    isChristmas={false}
                                    index={index}
                                    active={active}
                                    box={box}
                                    removeBox={this.removeBox}
                                    boxes={boxes}
                                    setActive={this.setActive}
                                    changeOrder={this.changeOrder}
                                    removeRhyt={this.removeRhyt}
                                    edit
                                    onLinebreakClick={this.onLinebreakClick}
                                    isLineBreak={breaks.indexOf(index) > -1}
                                />
                                {breaks.indexOf(index) !== -1 && <Break />}
                            </Fragment>
                        );
                    })}
                    {boxes[boxes.length - 1].length !== 0 && (
                        <StyledBar
                            className={cn("Edit")}
                            onClick={() => {
                                this.addBox();
                            }}
                        >
                            <AddBox>
                                <FormattedMessage {...messages.addCard} />
                            </AddBox>
                        </StyledBar>
                    )}
                </Bars>

                <Btns>
                    {Object.keys(rhytCardTypes).map((rhyt, index) => {
                        const Rhythm = Rhythms["rhyt_" + rhyt.replace(/-/g, "_")];
                        return (
                            <Btn
                                key={index}
                                onClick={() => {
                                    this.addRhytToBox(rhyt);
                                }}
                            >
                                <div
                                    style={{
                                        marginLeft: rhytCardTypes[rhyt].os + "%",
                                    }}
                                >
                                    <Rhythm />
                                </div>
                            </Btn>
                        );
                    })}
                </Btns>
            </Editor>
        );
    }
}

export default RhytCardEditor;
